import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { API_ENDPOINTS } from "../config/apiConfig";
import { LoadingSpinner } from "../components/general/LoadingSpinner";
import { getPlanById } from "../data/subscriptionPlans";
import { FaCheckCircle, FaArrowRight } from "react-icons/fa";

const PUBLIC_CHECKOUT_METADATA_KEY = 'amptrans_public_checkout_metadata';

const formatDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date.toLocaleDateString();
};

function SubscriptionOnboarding() {
  const [context, setContext] = useState(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState("idle"); // idle | submitting | success
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (typeof window === "undefined") return;
    let merged = null;
    try {
      const stored = sessionStorage.getItem(PUBLIC_CHECKOUT_METADATA_KEY);
      if (stored) {
        merged = JSON.parse(stored);
      }
    } catch (parseError) {
      console.error("Failed to parse onboarding context", parseError);
    }

    const params = new URLSearchParams(location.search);
    if ([...params.keys()].length > 0) {
      merged = {
        ...(merged || {}),
        checkoutSessionId: params.get("session_id") || params.get("sessionId") || merged?.checkoutSessionId,
        tempUserId: params.get("temp_user_id") || params.get("tempUserId") || merged?.tempUserId,
        email: params.get("email") || merged?.email,
        planId: params.get("plan_id") || params.get("planId") || merged?.planId
      };
    }

    if (merged) {
      setContext(merged);
      sessionStorage.setItem(PUBLIC_CHECKOUT_METADATA_KEY, JSON.stringify(merged));
      if (merged.firstName) setFirstName(merged.firstName);
      if (merged.lastName) setLastName(merged.lastName);
      if (merged.username) setUsername(merged.username);
      if (merged.email && !username) {
        const [localPart] = merged.email.split("@");
        if (localPart) setUsername(localPart.replace(/[^a-zA-Z0-9_\-]/g, "").slice(0, 20));
      }
    }
  }, [location.search]);

  const clearContext = useCallback(() => {
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(PUBLIC_CHECKOUT_METADATA_KEY);
    }
  }, []);

  const handleComplete = async (event) => {
    event.preventDefault();
    if (!context?.tempUserId) {
      setError("Missing enrollment information. Please contact support.");
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setError("First and last name are required.");
      return;
    }
    if (!username.trim() || username.length < 3) {
      setError("Username must be at least 3 characters.");
      return;
    }
    if (!/^[a-zA-Z0-9_\-]+$/.test(username)) {
      setError("Username can only include letters, numbers, underscores, or hyphens.");
      return;
    }
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setStatus("submitting");
    setError(null);

    try {
      await axios.post(API_ENDPOINTS.completePublicOnboarding, {
        temp_user_id: context.tempUserId,
        password,
        checkout_session_id: context.checkoutSessionId,
        email: context.email,
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        username: username.trim()
      });
      setStatus("success");
      clearContext();
    } catch (err) {
      console.error("Failed to complete onboarding", err.response?.data || err.message);
      const message =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Unable to complete onboarding. Please try again.";
      setError(message);
      setStatus("idle");
    }
  };

  const goToLogin = () => {
    const prefill = context?.email ? `?prefill=${encodeURIComponent(context.email)}` : "";
    navigate(`/login${prefill}`);
  };

  const plan = context?.planId ? getPlanById(context.planId) : null;

  if (!context && status !== "success") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30">
        <div className="max-w-3xl mx-auto px-6 py-24 text-center font-ui">
          <div className="rounded-3xl border border-slate-200 bg-white p-10 shadow-xl">
            <h1 className="mb-4 text-2xl font-bold text-slate-900">
              We couldn't find your checkout session
            </h1>
            <p className="mb-6 text-slate-600">
              Please return to the subscription page and restart checkout. Reach out to
              info@amptrans.net if the issue persists.
            </p>
            <button
              onClick={() => navigate("/subscription")}
              className="rounded-lg bg-blue-700 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-800"
            >
              Back to plans
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/40 to-teal-50/30 pt-4">
      <div className="mx-auto max-w-5xl px-6 py-16 font-ui lg:px-8">
        <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-xl">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-500 px-8 py-8 text-white">
            <div className="flex flex-wrap items-start justify-between gap-6">
              <div>
                <p className="mb-2 text-xs uppercase tracking-[0.24em] text-blue-100">
                  Subscription activated
                </p>
                <h1 className="mb-2 text-3xl font-bold">
                  You're all set.
                </h1>
                <p className="text-sm text-blue-100">
                  {plan ? `Your ${plan.name} license is active.` : "Your license is active."}
                </p>
                {context?.email && (
                  <p className="mt-2 text-sm text-blue-100">
                    Workspace email: <span className="font-semibold">{context.email}</span>
                  </p>
                )}
              </div>
              <div className="min-w-[220px] rounded-2xl border border-white/15 bg-white/10 px-6 py-4 backdrop-blur-sm">
                <p className="mb-1 text-xs uppercase tracking-[0.24em] text-blue-100">
                  Plan summary
                </p>
                <p className="mb-1 text-xl font-bold">
                  {plan ? plan.name : "Custom plan"}
                </p>
                {plan?.price && plan.price !== "Contact Us" ? (
                  <p className="text-blue-100">${plan.price}/month</p>
                ) : (
                  <p className="text-blue-100">Custom pricing</p>
                )}
                {context?.activationDate && (
                  <p className="mt-2 text-xs text-blue-100">
                    Activated {formatDate(context.activationDate)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {status === "submitting" && (
            <div className="p-8">
              <LoadingSpinner />
            </div>
          )}

          {status !== "submitting" && (
            <div className="flex flex-col items-center space-y-8 p-8">
              {status === "success" ? (
                <div className="w-full max-w-2xl space-y-4 rounded-3xl border border-slate-200 bg-slate-50 p-8 text-center shadow-sm">
                  <FaCheckCircle className="mx-auto h-12 w-12 text-emerald-500" />
                  <h2 className="text-2xl font-bold text-slate-900">Password created</h2>
                  <p className="text-slate-600">
                    You can now sign in using your new password.
                  </p>
                  <button
                    onClick={goToLogin}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-blue-700 px-6 py-3 font-semibold text-white transition-colors hover:bg-blue-800"
                  >
                    Go to login
                    <FaArrowRight className="ml-2" />
                  </button>
                </div>
              ) : (
                <form className="w-full max-w-3xl space-y-6 rounded-3xl border border-slate-200 bg-white p-8 shadow-sm" onSubmit={handleComplete}>
                  <div className="text-center">
                    <p className="text-sm font-semibold uppercase tracking-[0.24em] text-blue-700">
                      Finish onboarding
                    </p>
                    <p className="mt-3 text-lg font-semibold text-slate-900">
                      Complete these details to access your AmpTrans portal.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        First name
                      </label>
                      <input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-slate-700">
                        Last name
                      </label>
                      <input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Username
                    </label>
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={3}
                      maxLength={25}
                    />
                    <p className="mt-1 text-xs text-slate-500">
                      Letters, numbers, underscores, or hyphens (3–25 characters).
                    </p>
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Create a password
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={8}
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      Confirm password
                    </label>
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full rounded-xl border border-slate-200 px-4 py-3 text-base text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                      minLength={8}
                    />
                  </div>
                  {error && (
                    <div className="rounded-lg bg-red-50 p-3 text-sm text-red-600">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-700 px-6 py-4 text-lg font-semibold text-white transition-colors hover:bg-blue-800"
                  >
                    Finish setup
                  </button>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SubscriptionOnboarding;

